import { useState } from "react";
import {
  Alert, Box, Button, Chip, IconButton, MenuItem, Paper, Stack,
  Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from "@mui/material";
import ReplayIcon from "@mui/icons-material/Replay";
import { DataGrid, type GridColDef, type GridPaginationModel } from "@mui/x-data-grid";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  healnTrackApi, type HealNTrackDirection, type HealNTrackEventRow,
  type HealNTrackEventStatus, type HealNTrackOpenChargeDto,
} from "@/api/endpoints";
import { errorMessage } from "@/api/client";
import { useAuth } from "@/store/auth";

const DIRECTIONS: ("" | HealNTrackDirection)[] = ["", "Inbound", "Outbound"];
const STATUSES: ("" | HealNTrackEventStatus)[] = ["", "Pending", "Processed", "Failed", "Dead"];

const STATUS_COLOR: Record<HealNTrackEventStatus, "default" | "warning" | "success" | "error"> = {
  Pending: "default", Processed: "success", Failed: "warning", Dead: "error",
};

/**
 * S12 — HealNTrack event log + replay. Combines inbound webhook deliveries
 * and outbound queued events; sync-permission gates the page. The open-charges
 * widget lets ops staff verify discharge reconciliation by UHID.
 */
export function HealNTrackPage() {
  const qc = useQueryClient();
  const canReplay = useAuth(s => s.hasPermission("integration.healntrack.sync"));

  const [direction, setDirection] = useState<"" | HealNTrackDirection>("");
  const [status, setStatus] = useState<"" | HealNTrackEventStatus>("");
  const [uhid, setUhid] = useState("");
  const [pagination, setPagination] = useState<GridPaginationModel>({ page: 0, pageSize: 25 });

  const query = useQuery({
    queryKey: ["healntrack-events", direction, status, uhid, pagination.page, pagination.pageSize],
    queryFn: () => healnTrackApi.list({
      direction: direction || undefined, status: status || undefined,
      uhid: uhid || undefined,
      page: pagination.page + 1, pageSize: pagination.pageSize,
    }),
  });

  const replayMut = useMutation({
    mutationFn: (id: string) => healnTrackApi.replay(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["healntrack-events"] }),
  });

  // Discharge reconciliation widget
  const [chargesUhid, setChargesUhid] = useState("");
  const [charges, setCharges] = useState<HealNTrackOpenChargeDto[] | null>(null);
  const [chargesError, setChargesError] = useState<string | null>(null);
  const loadCharges = async () => {
    setChargesError(null);
    try { setCharges(await healnTrackApi.openCharges(chargesUhid)); }
    catch (err) { setChargesError(errorMessage(err)); setCharges(null); }
  };

  const columns: GridColDef<HealNTrackEventRow>[] = [
    { field: "at", headerName: "At", width: 170,
      valueFormatter: (v) => v ? new Date(v as string).toLocaleString() : "" },
    { field: "direction", headerName: "Dir", width: 90,
      renderCell: (p) => <Chip size="small" label={p.value as string}
        color={p.value === "Inbound" ? "info" : "secondary"} /> },
    { field: "eventType", headerName: "Event", width: 200 },
    { field: "uhid", headerName: "UHID", width: 140 },
    { field: "admissionId", headerName: "Admission", width: 140 },
    { field: "status", headerName: "Status", width: 110,
      renderCell: (p) => <Chip size="small" label={p.value as string}
        color={STATUS_COLOR[p.value as HealNTrackEventStatus] ?? "default"} /> },
    { field: "attemptCount", headerName: "Tries", width: 70, type: "number" },
    { field: "lastError", headerName: "Last error", flex: 1, minWidth: 240 },
    { field: "actions", headerName: "", width: 60, sortable: false, filterable: false,
      renderCell: (p) => canReplay ? (
        <IconButton size="small" disabled={replayMut.isPending}
          onClick={() => replayMut.mutate(p.row.id)}>
          <ReplayIcon fontSize="small" />
        </IconButton>
      ) : null },
  ];

  return (
    <Box>
      <Typography variant="h5" fontWeight={600} sx={{ mb: 2 }}>HealNTrack integration</Typography>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
          <TextField select label="Direction" value={direction} size="small"
            onChange={(e) => setDirection(e.target.value as "" | HealNTrackDirection)} sx={{ minWidth: 140 }}>
            {DIRECTIONS.map(d => <MenuItem key={d} value={d}>{d || "All"}</MenuItem>)}
          </TextField>
          <TextField select label="Status" value={status} size="small"
            onChange={(e) => setStatus(e.target.value as "" | HealNTrackEventStatus)} sx={{ minWidth: 140 }}>
            {STATUSES.map(s => <MenuItem key={s} value={s}>{s || "All"}</MenuItem>)}
          </TextField>
          <TextField label="UHID" value={uhid} size="small"
            onChange={(e) => setUhid(e.target.value)} sx={{ minWidth: 180 }} />
        </Stack>
        {(query.isError || replayMut.isError) && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errorMessage(query.error ?? replayMut.error)}
          </Alert>
        )}
        <Box sx={{ height: 540 }}>
          <DataGrid
            rows={query.data?.items ?? []} columns={columns} loading={query.isLoading}
            paginationMode="server" rowCount={query.data?.totalCount ?? 0}
            paginationModel={pagination} onPaginationModelChange={setPagination}
            pageSizeOptions={[10, 25, 50, 100]} disableRowSelectionOnClick
            getRowId={(row) => row.id}
          />
        </Box>
      </Paper>

      <Paper sx={{ p: 2 }}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
          Discharge reconciliation
        </Typography>
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
          <TextField label="UHID" value={chargesUhid} size="small"
            onChange={(e) => setChargesUhid(e.target.value)} sx={{ minWidth: 220 }} />
          <Button variant="contained" onClick={loadCharges} disabled={!chargesUhid}>
            Find open charges
          </Button>
        </Stack>
        {chargesError && <Alert severity="error" sx={{ mb: 2 }}>{chargesError}</Alert>}
        {charges !== null && (
          charges.length === 0 ? (
            <Alert severity="success">No open charges — discharge can proceed.</Alert>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Invoice #</TableCell><TableCell>Date</TableCell>
                  <TableCell align="right">Total</TableCell>
                  <TableCell align="right">Paid</TableCell>
                  <TableCell align="right">Outstanding</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {charges.map((c) => (
                  <TableRow key={c.salesInvoiceId}>
                    <TableCell>{c.invoiceNo}</TableCell>
                    <TableCell>{new Date(c.invoiceDate).toLocaleDateString()}</TableCell>
                    <TableCell align="right">{c.total.toFixed(2)}</TableCell>
                    <TableCell align="right">{c.paid.toFixed(2)}</TableCell>
                    <TableCell align="right">
                      <Chip size="small" color="error" label={c.outstanding.toFixed(2)} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )
        )}
      </Paper>
    </Box>
  );
}
